import dayjs from 'dayjs'

// ===================== 常量 =====================

// 中文数字 → 阿拉伯数字
const CN_NUM_MAP = {
  '零': 0, '〇': 0,
  '一': 1, '二': 2, '三': 3, '四': 4, '五': 5,
  '六': 6, '七': 7, '八': 8, '九': 9, '十': 10,
  '十一': 11, '十二': 12, '十三': 13, '十四': 14, '十五': 15,
  '十六': 16, '十七': 17, '十八': 18, '十九': 19, '二十': 20,
  '二十一': 21, '二十二': 22, '二十三': 23, '二十四': 24, '二十五': 25,
  '二十六': 26, '二十七': 27, '二十八': 28, '二十九': 29, '三十': 30, '三十一': 31,
}

// 比率关键词
const RATE_MAP = [
  { pattern: /一半|1\/2|二分之一|50%/, value: 0.5, label: '×0.5' },
  { pattern: /两倍|2倍|二倍/, value: 2, label: '×2' },
  { pattern: /三分之一|1\/3/, value: 1 / 3, label: '×0.33' },
  { pattern: /三倍|3倍/, value: 3, label: '×3' },
  { pattern: /四分之一|1\/4|25%/, value: 0.25, label: '×0.25' },
  { pattern: /四分之三|3\/4|75%/, value: 0.75, label: '×0.75' },
]

// ===================== 工具函数 =====================

/** 中文数字/阿拉伯数字 → Number */
function parseCnNum(str) {
  if (/^\d+$/.test(str)) return parseInt(str)
  return CN_NUM_MAP[str] || null
}

/** 单个日期项 → guba 取值表达式 */
function buildItemStr(item) {
  const base = `(guba['${item.date}']?.count || 0)`
  return item.rate !== 1 ? `(${base} * ${item.rate})` : base
}

/** 多日期 → 聚合表达式 (sum / avg) */
function buildMultiStr(items, aggType) {
  const parts = items.map(i => buildItemStr(i))
  if (aggType === 'avg') {
    return `((${parts.join(' + ')}) / ${items.length})`
  }
  // sum
  return `(${parts.join(' + ')})`
}

/** 多日期 → 可读标签 */
function buildMultiLabel(items, aggType) {
  const labels = items.map(i => i.dateLabel || i.date)
  if (aggType === 'avg') {
    return `avg(${labels.join(', ')})`
  }
  return `(${labels.join(' + ')})`
}

// ===================== 日期 & 比率提取 =====================

/**
 * 从文本片段中提取【单个日期】和【比率】
 *
 * 支持的日期格式（按优先级）：
 *   YYYY-MM-DD   "2026-02-09"
 *   N月N号/N月N日 "2月9号"
 *   M-D / MM-DD   "2-9" / "02-09"
 *   N号 / N日     "9号" / "6日" / "九号" / "六日"
 *   相对日期       今天 / 昨天 / 前天 / 上一个交易日
 *   序号引用       第1个 / 第二个
 *   关键词         最新 / 最近 / 最后
 */
function extractDateAndRate(text, dataType) {
  let date = null
  let rate = 1
  let rateLabel = ''

  const clean = text.replace(/的$/, '').trim()

  // -------- 日期 --------

  // 1. 完整日期 YYYY-MM-DD
  const fullMatch = clean.match(/(\d{4})-(\d{1,2})-(\d{1,2})/)
  if (fullMatch) {
    date = `${fullMatch[1]}-${fullMatch[2].padStart(2, '0')}-${fullMatch[3].padStart(2, '0')}`
  }

  // 2. N月N号 / N月N日（中文数字或阿拉伯数字）
  if (!date) {
    const mdMatch = clean.match(/([\d一二三四五六七八九十]+)月([\d一二三四五六七八九十]+)[号日]/)
    if (mdMatch) {
      const month = parseCnNum(mdMatch[1])
      const day = parseCnNum(mdMatch[2])
      if (month && day) {
        const fullDate = `${dayjs().year()}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
        // 优先匹配 dataType
        date = dataType?.find(d => d === fullDate) || fullDate
      }
    }
  }

  // 3. 短格式 M-D / MM-DD（自动补年份）
  if (!date) {
    const shortMatch = clean.match(/(\d{1,2})-(\d{1,2})/)
    if (shortMatch && !clean.match(/\d{4}-/)) {
      date = `${dayjs().year()}-${shortMatch[1].padStart(2, '0')}-${shortMatch[2].padStart(2, '0')}`
    }
  }

  // 4. N号 / N日（阿拉伯或中文数字，仅天数，从 dataType 中匹配）
  if (!date) {
    const dayMatch = clean.match(/([\d一二三四五六七八九十]+)[号日]/)
    if (dayMatch) {
      const dayNum = parseCnNum(dayMatch[1])
      if (dayNum) {
        const dayStr = String(dayNum).padStart(2, '0')
        // 在 dataType 中找结尾匹配的日期（取最近的）
        date = dataType?.find(d => d.endsWith(`-${dayStr}`)) || null
        if (!date) {
          // 回退：当前年月 + 日
          date = `${dayjs().format('YYYY-MM')}-${dayStr}`
        }
      }
    }
  }

  // 5. 相对日期关键词
  if (!date) {
    if (/今天|今日|当天|当日/.test(clean)) {
      date = resolveRelativeDate(0, dataType)
    } else if (/昨天|昨日/.test(clean)) {
      date = resolveRelativeDate(1, dataType)
    } else if (/前天|前日/.test(clean)) {
      date = resolveRelativeDate(2, dataType)
    } else if (/上一个交易日|上个交易日|前一个交易日/.test(clean)) {
      date = resolveRelativeDate(1, dataType)
    }
  }

  // 6. "第N个"（中文数字或阿拉伯数字）
  if (!date) {
    const idxMatch = clean.match(/第([一二三四五六七八九十\d]+)个/)
    if (idxMatch) {
      const n = parseCnNum(idxMatch[1])
      if (n && dataType && n - 1 < dataType.length) {
        date = dataType[n - 1]
      }
    }
  }

  // 7. "最新" / "最近" / "最后" → dataType[0]
  if (!date) {
    if (/最新|最近|最后/.test(clean)) {
      date = dataType?.[0] || null
    }
  }

  // -------- 比率 --------
  for (const r of RATE_MAP) {
    if (r.pattern.test(clean)) {
      rate = r.value
      rateLabel = r.label
      break
    }
  }
  if (rate === 1) rateLabel = ''

  return { date, rate, rateLabel }
}

/** 相对日期 → 具体日期字符串 */
function resolveRelativeDate(offset, dataType) {
  if (dataType && dataType.length > offset) {
    return dataType[offset]
  }
  return dayjs().subtract(offset, 'day').format('YYYY-MM-DD')
}

// ===================== 表达式解析 =====================

/**
 * 解析一侧的表达式
 *
 * 返回：
 *   items:    [{ date, rate, dateLabel }]
 *   aggType:  'none' | 'sum' | 'avg' | 'each'
 *   funcStr:  生成的 JS 表达式（'each' 模式下为 null，由主函数根据方向决定）
 *   label:    可读描述
 *
 * 支持：
 *   单日期:     "9号" / "今天" / "6日的一半"
 *   多日期求和:  "6号和5号加起来" / "6号+5号" / "6号加5号"
 *   多日期平均:  "6号和5号的平均"
 *   多日期逐个:  "6号和5号"（软连接词，默认逐个比较）
 *   最近N天:    "最近3天" / "最近三天的平均"
 */
function parseExpression(text, dataType) {
  // 1. 检测显式聚合关键词
  let aggType = 'none'
  let cleanText = text

  if (/加起来|总和|之和|总计|相加/.test(text)) {
    aggType = 'sum'
    cleanText = text.replace(/的?(加起来|总和|之和|总计|相加)/g, '').trim()
  } else if (/平均值?|均值/i.test(text)) {
    aggType = 'avg'
    cleanText = text.replace(/的?(平均值?|均值)/gi, '').trim()
  }

  // 去掉尾部的"的"
  cleanText = cleanText.replace(/的$/, '').trim()

  // 2. "最近N天" / "前N天" / "近N天" / "过去N天" 组表达式
  const recentMatch = cleanText.match(/(最近|前|近|过去)(\d+|[一二三四五六七八九十]+)[天个日]/)
  if (recentMatch && dataType?.length) {
    const keyword = recentMatch[1]
    const n = parseCnNum(recentMatch[2])
    // "前/过去" → 不含今天，从第2个开始取；"最近/近" → 含今天，从第1个开始取
    const excludeToday = /前|过去/.test(keyword)
    const startIdx = excludeToday ? 1 : 0
    if (n && startIdx + n <= dataType.length) {
      const items = dataType.slice(startIdx, startIdx + n).map(d => ({
        date: d, rate: 1, dateLabel: d,
      }))
      // 没有显式聚合时默认取平均
      if (aggType === 'none') aggType = 'avg'
      const prefix = excludeToday ? '前' : '最近'
      return {
        items,
        aggType,
        funcStr: buildMultiStr(items, aggType),
        label: `${prefix}${n}天${aggType === 'avg' ? '平均' : '总和'}`,
      }
    }
  }

  // 3. 检查多日期连接词
  //    软连接 "和/跟/与" → 默认逐个比较 (each)
  //    硬连接 "加/+/＋" → 默认求和 (sum)
  const hasSoft = /和|跟|与/.test(cleanText)
  const hasHard = /[+＋]/.test(cleanText) || /加(?!起来)/.test(cleanText)

  if (hasSoft || hasHard) {
    // 按连接词分割
    const parts = cleanText
      .split(/[和跟与＋+]|加(?!起来)/)
      .map(p => p.trim())
      .filter(Boolean)

    if (parts.length >= 2) {
      const items = []
      for (const part of parts) {
        const result = extractDateAndRate(part, dataType)
        if (result.date) {
          items.push({
            date: result.date,
            rate: result.rate,
            dateLabel: result.date + (result.rateLabel || ''),
          })
        }
      }

      if (items.length >= 2) {
        // 决定聚合方式：显式 > 硬连接(sum) > 软连接(each)
        if (aggType === 'none') {
          aggType = hasHard ? 'sum' : 'each'
        }

        if (aggType === 'each') {
          // each 模式：funcStr 留空，由主函数按比较方向生成 Math.max/min
          return {
            items,
            aggType,
            funcStr: null,
            label: items.map(i => i.dateLabel).join('和'),
          }
        }

        return {
          items,
          aggType,
          funcStr: buildMultiStr(items, aggType),
          label: buildMultiLabel(items, aggType),
        }
      }
    }
  }

  // 4. 单日期 + 比率
  const single = extractDateAndRate(cleanText, dataType)
  if (single.date) {
    const item = {
      date: single.date,
      rate: single.rate,
      dateLabel: single.date + (single.rateLabel || ''),
    }
    return {
      items: [item],
      aggType: 'none',
      funcStr: buildItemStr(item),
      label: item.dateLabel,
    }
  }

  return { items: [], aggType: 'none', funcStr: '', label: '' }
}

// ===================== 主函数 =====================

/**
 * 解析用户自然语言，生成特征过滤函数字符串
 *
 * 支持的句式：
 *   "9号比6号多"                        ← N号日期格式
 *   "九号比六号少"                      ← 中文数字日号
 *   "今天比6日的一半多"                 ← 混合格式 + 比率
 *   "9号比6号和5号的加起来都多"          ← 多日期求和
 *   "9号比6号和5号都多"                 ← 逐个比较(each)
 *   "9号比6号和5号的平均多"             ← 多日期平均
 *   "9号比6号加5号多"                   ← "加" = 求和
 *   "9号比6号+5号多"                    ← "+" = 求和
 *   "2月9号比2月6号多"                  ← N月N号格式
 *   "今天比最近3天的平均多"             ← 最近N天
 *   "第1个比第2个的一半少"              ← 序号引用 + 比率
 *   "2026-02-09比2026-02-06多"         ← 完整日期
 *   "6号和5号比9号少"                   ← 左侧多日期
 *
 * @param {string} prompt - 用户输入的自然语言
 * @param {string[]} dataType - 已选日期列表（从新到旧）
 * @returns {{ feature: string, label: string, error: string|null }}
 */
export function parseUserPrompt(prompt, dataType = []) {
  if (!prompt || !prompt.trim()) {
    return { feature: '', label: '', error: '请输入筛选条件' }
  }

  // 预处理：统一标点、去无关填充词
  let text = prompt.trim()
    .replace(/[，。！？;\s]+/g, ' ')
    .replace(/的?帖子数量?/g, '')
    .replace(/的?数量/g, '')
    .trim()

  // ① 用"比"分割左右两侧
  const biMatch = text.match(/^(.+?)比(.+)$/)
  if (!biMatch) {
    return { feature: '', label: '', error: '未识别到"比"字，请使用"A比B多/少"的格式' }
  }

  let leftText = biMatch[1].trim()
  let rightText = biMatch[2].trim()

  // ② 从右侧末尾提取比较关系（兼容 都/还/要/也 等修饰词）
  let comparison = null
  let compLabel = ''

  const compMatch = rightText.match(/[，, ]*((?:都|还|要|也)*(?:多|少))\s*$/)
  if (compMatch) {
    if (/多/.test(compMatch[1])) {
      comparison = '> 0'
      compLabel = '多'
    } else {
      comparison = '< 0'
      compLabel = '少'
    }
    rightText = rightText.slice(0, rightText.length - compMatch[0].length).trim()
  }

  if (!comparison) {
    return { feature: '', label: '', error: '未识别到比较关系，请在末尾加上"多"或"少"' }
  }

  // ③ 解析左右表达式
  const left = parseExpression(leftText, dataType)
  const right = parseExpression(rightText, dataType)

  if (!left.items.length) {
    return { feature: '', label: '', error: `未能解析左侧: "${leftText}"` }
  }
  if (!right.items.length) {
    return { feature: '', label: '', error: `未能解析右侧: "${rightText}"` }
  }

  // ④ 处理 each 聚合（需要根据比较方向决定 max / min）
  let leftFuncStr = left.funcStr
  let rightFuncStr = right.funcStr
  let leftLabel = left.label
  let rightLabel = right.label

  //  右侧 each: "比A和B多" → left > max(A,B); "比A和B少" → left < min(A,B)
  if (right.aggType === 'each' && right.items.length > 1) {
    const fn = comparison === '> 0' ? 'Math.max' : 'Math.min'
    rightFuncStr = `${fn}(${right.items.map(i => buildItemStr(i)).join(', ')})`
    rightLabel = right.items.map(i => i.dateLabel).join('和') + '(每个)'
  }

  //  左侧 each: "A和B比C多" → min(A,B) > C; "A和B比C少" → max(A,B) < C
  if (left.aggType === 'each' && left.items.length > 1) {
    const fn = comparison === '> 0' ? 'Math.min' : 'Math.max'
    leftFuncStr = `${fn}(${left.items.map(i => buildItemStr(i)).join(', ')})`
    leftLabel = left.items.map(i => i.dateLabel).join('和') + '(每个)'
  }

  // ⑤ 验证日期是否在 dataType 中
  const warnings = []
  if (dataType.length > 0) {
    const allDates = [...new Set([...left.items, ...right.items].map(i => i.date))]
    for (const d of allDates) {
      if (!dataType.includes(d)) {
        warnings.push(`"${d}" 不在已选日期中`)
      }
    }
  }

  // ⑥ 生成可执行函数字符串
  const feature = `(guba) => ${leftFuncStr} - ${rightFuncStr} ${comparison}`
  const label = `${leftLabel} 比 ${rightLabel} ${compLabel}`

  return {
    feature,
    label,
    error: warnings.length > 0 ? `⚠️ ${warnings.join('；')}` : null,
  }
}

// ===================== 兼容旧版 =====================

/**
 * 根据 dataType 生成快捷 featureOptions（用于标签点击）
 */
export function generateFeatureOptions(dataType) {
  if (!Array.isArray(dataType) || dataType.length < 2) {
    return []
  }

  const options = []
  for (let i = 0; i < dataType.length - 1; i++) {
    const date1 = dataType[i]
    const date2 = dataType[i + 1]

    options.push({
      label: `${date1}比${date2}多`,
      value: `(guba) => (guba['${date1}']?.count || 0) - (guba['${date2}']?.count || 0) > 0`,
    })
    options.push({
      label: `${date1}比${date2}少`,
      value: `(guba) => (guba['${date1}']?.count || 0) - (guba['${date2}']?.count || 0) < 0`,
    })
  }

  return options
}
